import { useCallback, useEffect, useMemo, useRef } from "react";
import { studyViewportTelemetry } from "./viewport";

function monotonicNow() {
  if (typeof performance !== "undefined" && typeof performance.now === "function") {
    return performance.now();
  }
  return Date.now();
}

function pageIsVisible() {
  return typeof document === "undefined" || document.visibilityState !== "hidden";
}

function pageIsFocused() {
  return pageIsVisible()
    && (typeof document === "undefined" || typeof document.hasFocus !== "function" || document.hasFocus());
}

function clientSessionId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `study-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function eventId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `event-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

const TELEMETRY_SCHEMA_VERSION = "chi2027-events-v0.2";
const TELEMETRY_BATCH_SIZE = 100;
const CRITICAL_EVENT_TYPES = new Set([
  "task_start",
  "conversation_turn_sent",
  "verification_card_rendered",
  "review_status_changed",
  "mismatch_action_changed",
  "field_edit_started",
  "field_edit_committed",
  "progress_attempt_blocked",
  "verification_stage_completed",
  "final_review_opened",
  "final_field_edit_committed",
  "submit_blocked_incomplete",
  "submit_attempt",
  "submission_locked",
  "study_complete",
  "client_session_end",
]);

function rounded(value) {
  return Math.max(0, Math.round(value || 0));
}

function bestEffort(promise) {
  if (promise && typeof promise.catch === "function") promise.catch(() => {});
}

/**
 * Adds a monotonic client sequence and three timing clocks to each event:
 * wall time, foreground time, and foreground+focused active time. The task
 * clocks start only after the study socket is connected.
 */
export function useStudyTelemetry(
  postTelemetry,
  { studyProtocolVersion = "unversioned", studyBuildId = null } = {}
) {
  const clockRef = useRef(null);
  const pendingRef = useRef([]);
  const flushingRef = useRef(null);
  const retryTimerRef = useRef(null);
  const retryDelayRef = useRef(1000);
  const retryFlushRef = useRef(null);
  if (clockRef.current === null) {
    const now = monotonicNow();
    const visible = pageIsVisible();
    const focused = pageIsFocused();
    clockRef.current = {
      clientSessionId: clientSessionId(),
      sequence: 0,
      mountedAt: now,
      visible,
      visibleStartedAt: visible ? now : null,
      foregroundMs: 0,
      focused,
      focusedStartedAt: focused ? now : null,
      activeMs: 0,
      taskStartedAt: null,
      taskForegroundAtStart: null,
      taskActiveAtStart: null,
    };
  }

  const snapshot = useCallback((at = monotonicNow()) => {
    const clock = clockRef.current;
    const foregroundMs = clock.foregroundMs
      + (clock.visible && clock.visibleStartedAt !== null ? at - clock.visibleStartedAt : 0);
    const activeMs = clock.activeMs
      + (clock.focused && clock.focusedStartedAt !== null ? at - clock.focusedStartedAt : 0);
    const taskStarted = clock.taskStartedAt !== null;
    return {
      client_elapsed_ms: rounded(at - clock.mountedAt),
      foreground_elapsed_ms: rounded(foregroundMs),
      active_elapsed_ms: rounded(activeMs),
      ...(taskStarted
        ? {
            task_elapsed_ms: rounded(at - clock.taskStartedAt),
            foreground_task_elapsed_ms: rounded(foregroundMs - clock.taskForegroundAtStart),
            active_task_elapsed_ms: rounded(activeMs - clock.taskActiveAtStart),
          }
        : {}),
    };
  }, []);

  const transitionClock = useCallback((at = monotonicNow()) => {
    const clock = clockRef.current;
    const visible = pageIsVisible();
    const focused = pageIsFocused();

    if (clock.visible && !visible && clock.visibleStartedAt !== null) {
      clock.foregroundMs += at - clock.visibleStartedAt;
      clock.visibleStartedAt = null;
    } else if (!clock.visible && visible) {
      clock.visibleStartedAt = at;
    }
    clock.visible = visible;

    if (clock.focused && !focused && clock.focusedStartedAt !== null) {
      clock.activeMs += at - clock.focusedStartedAt;
      clock.focusedStartedAt = null;
    } else if (!clock.focused && focused) {
      clock.focusedStartedAt = at;
    }
    clock.focused = focused;
  }, []);

  const scheduleRetry = useCallback(() => {
    if (retryTimerRef.current !== null || pendingRef.current.length === 0) return;
    const delay = retryDelayRef.current;
    retryDelayRef.current = Math.min(delay * 2, 10000);
    retryTimerRef.current = setTimeout(() => {
      retryTimerRef.current = null;
      bestEffort(retryFlushRef.current?.());
    }, delay);
  }, []);

  const flushPending = useCallback(() => {
    if (flushingRef.current) return flushingRef.current;
    const run = (async () => {
      while (pendingRef.current.length > 0) {
        const batch = pendingRef.current.slice(0, TELEMETRY_BATCH_SIZE);
        await postTelemetry(batch.length === 1 ? batch[0] : batch);
        const sentEventIds = new Set(batch.map((event) => event.event_id));
        pendingRef.current = pendingRef.current.filter(
          (event) => !sentEventIds.has(event.event_id)
        );
      }
      return null;
    })();
    flushingRef.current = run
      .then((result) => {
        retryDelayRef.current = 1000;
        return result;
      })
      .catch((error) => {
        scheduleRetry();
        throw error;
      })
      .finally(() => {
        flushingRef.current = null;
      });
    return flushingRef.current;
  }, [postTelemetry, scheduleRetry]);
  retryFlushRef.current = flushPending;

  const send = useCallback((event) => {
    const at = monotonicNow();
    transitionClock(at);
    const clock = clockRef.current;
    clock.sequence += 1;
    const envelope = {
      ...event,
      ...studyViewportTelemetry(),
      telemetry_schema_version: TELEMETRY_SCHEMA_VERSION,
      study_protocol_version: studyProtocolVersion,
      ...(studyBuildId ? { study_build_id: studyBuildId } : {}),
      event_id: event?.event_id || eventId(),
      client_session_id: clock.clientSessionId,
      client_event_seq: clock.sequence,
      client_ts: new Date().toISOString(),
      ...snapshot(at),
    };
    if (CRITICAL_EVENT_TYPES.has(envelope.event_type)) {
      return postTelemetry(envelope).catch((error) => {
        pendingRef.current.unshift(envelope);
        scheduleRetry();
        throw error;
      });
    }
    pendingRef.current.push(envelope);
    return flushPending();
  }, [
    flushPending,
    postTelemetry,
    scheduleRetry,
    snapshot,
    studyBuildId,
    studyProtocolVersion,
    transitionClock,
  ]);

  const startTask = useCallback((extra = {}) => {
    const at = monotonicNow();
    const clock = clockRef.current;
    if (clock.taskStartedAt !== null) return Promise.resolve(null);
    transitionClock(at);
    const totals = snapshot(at);
    clock.taskStartedAt = at;
    clock.taskForegroundAtStart = totals.foreground_elapsed_ms;
    clock.taskActiveAtStart = totals.active_elapsed_ms;
    return send({ event_type: "task_start", ...extra });
  }, [send, snapshot, transitionClock]);

  const checkpoint = useCallback(() => ({
    telemetry_schema_version: TELEMETRY_SCHEMA_VERSION,
    study_protocol_version: studyProtocolVersion,
    ...(studyBuildId ? { study_build_id: studyBuildId } : {}),
    client_session_id: clockRef.current.clientSessionId,
    last_client_event_seq: clockRef.current.sequence,
    pending_event_count: pendingRef.current.length,
  }), [studyBuildId, studyProtocolVersion]);

  useEffect(() => {
    bestEffort(send({ event_type: "client_session_start" }));

    const handleVisibility = () => {
      transitionClock();
      bestEffort(send({
        event_type: "page_visibility_change",
        visibility_state: document.visibilityState,
      }));
    };
    const handleFocus = () => {
      transitionClock();
      bestEffort(send({ event_type: "window_focus_change", focused: true }));
    };
    const handleBlur = () => {
      transitionClock();
      bestEffort(send({ event_type: "window_focus_change", focused: false }));
    };
    const handleUnload = () => {
      transitionClock();
      bestEffort(send({
        event_type: "client_session_end",
        reason: "page_unload",
        keepalive: true,
      }));
    };

    let resizeTimer = null;
    const handleResize = () => {
      if (resizeTimer !== null) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        resizeTimer = null;
        bestEffort(send({ event_type: "viewport_change" }));
      }, 150);
    };

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("pagehide", handleUnload);
    window.addEventListener("resize", handleResize);
    window.visualViewport?.addEventListener("resize", handleResize);
    return () => {
      if (resizeTimer !== null) clearTimeout(resizeTimer);
      if (retryTimerRef.current !== null) clearTimeout(retryTimerRef.current);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("pagehide", handleUnload);
      window.removeEventListener("resize", handleResize);
      window.visualViewport?.removeEventListener("resize", handleResize);
    };
  }, [send, transitionClock]);

  return useMemo(
    () => ({ send, startTask, snapshot, flushPending, checkpoint }),
    [checkpoint, flushPending, send, snapshot, startTask]
  );
}

function rectSnapshot(rect) {
  if (!rect) return null;
  return {
    x: Math.round(rect.x || 0),
    y: Math.round(rect.y || 0),
    width: Math.round(rect.width || 0),
    height: Math.round(rect.height || 0),
    top: Math.round(rect.top || 0),
    right: Math.round(rect.right || 0),
    bottom: Math.round(rect.bottom || 0),
    left: Math.round(rect.left || 0),
  };
}

function containerSnapshot(node, explicitContainer) {
  const container = explicitContainer
    || node?.closest?.(".study-chat-body, .scenario-panel")
    || null;
  if (!container) return null;
  return {
    rect: rectSnapshot(container.getBoundingClientRect?.()),
    scroll_top_px: Math.round(container.scrollTop || 0),
    scroll_left_px: Math.round(container.scrollLeft || 0),
    scroll_height_px: Math.round(container.scrollHeight || 0),
    scroll_width_px: Math.round(container.scrollWidth || 0),
    client_height_px: Math.round(container.clientHeight || 0),
    client_width_px: Math.round(container.clientWidth || 0),
  };
}

/**
 * Emits render and focused-viewport exposure intervals for a field or panel.
 * Dwell excludes hidden tabs and periods where the study frame is not focused.
 */
export function useVisibilityTelemetry({
  onTelemetry,
  eventPrefix,
  context = {},
  threshold = 0.5,
  thresholds = [0, 0.25, 0.5, 0.75, 1],
  geometryEventType,
  containerRef,
}) {
  const elementRef = useRef(null);
  const callbackRef = useRef(onTelemetry);
  const contextRef = useRef(context);
  callbackRef.current = onTelemetry;
  contextRef.current = context;
  const thresholdVector = Array.from(new Set(
    [...thresholds, threshold]
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value >= 0 && value <= 1)
  )).sort((left, right) => left - right);
  const thresholdKey = thresholdVector.join("|");

  useEffect(() => {
    const activeThresholdVector = thresholdKey.split("|").map(Number);
    const emit = (event) => {
      if (callbackRef.current) callbackRef.current({ ...event, ...contextRef.current });
    };
    const node = elementRef.current;
    let intersecting = false;
    let exposed = false;
    let exposedAt = null;
    let latestEntry = null;

    const emitGeometry = (reason, entry = latestEntry) => {
      if (!entry) return;
      const ratio = Number(entry.intersectionRatio || 0);
      emit({
        event_type: geometryEventType || `${eventPrefix}_geometry_change`,
        intersection_ratio: Math.round(ratio * 10000) / 10000,
        is_intersecting: Boolean(entry.isIntersecting),
        primary_opportunity_threshold: threshold,
        threshold_vector: activeThresholdVector,
        element_rect: rectSnapshot(entry.boundingClientRect),
        root_rect: rectSnapshot(entry.rootBounds),
        intersection_rect: rectSnapshot(entry.intersectionRect),
        container_geometry: containerSnapshot(node, containerRef?.current),
        page_visible: pageIsVisible(),
        window_focused: pageIsFocused(),
        geometry_change_reason: reason,
      });
    };

    const stopExposure = (reason) => {
      if (!exposed) return;
      const at = monotonicNow();
      exposed = false;
      emit({
        event_type: `${eventPrefix}_visibility_end`,
        visible_duration_ms: rounded(at - exposedAt),
        visibility_end_reason: reason,
      });
      exposedAt = null;
    };
    const reconcile = (reason) => {
      const shouldExpose = intersecting && pageIsFocused();
      if (shouldExpose && !exposed) {
        exposed = true;
        exposedAt = monotonicNow();
        emit({ event_type: `${eventPrefix}_visibility_start`, visibility_start_reason: reason });
      } else if (!shouldExpose && exposed) {
        stopExposure(reason);
      }
      if (reason === "viewport") emitGeometry(reason);
    };

    emit({
      event_type: `${eventPrefix}_rendered`,
      primary_opportunity_threshold: threshold,
      threshold_vector: activeThresholdVector,
    });
    if (!node || typeof IntersectionObserver === "undefined") return undefined;

    const observer = new IntersectionObserver((entries) => {
      const entry = entries[0];
      latestEntry = entry || null;
      intersecting = Boolean(entry?.isIntersecting && entry.intersectionRatio >= threshold);
      reconcile("viewport");
    }, { threshold: activeThresholdVector });
    const handlePageState = () => reconcile("page_state");
    observer.observe(node);
    document.addEventListener("visibilitychange", handlePageState);
    window.addEventListener("focus", handlePageState);
    window.addEventListener("blur", handlePageState);

    return () => {
      stopExposure("unmounted");
      observer.disconnect();
      document.removeEventListener("visibilitychange", handlePageState);
      window.removeEventListener("focus", handlePageState);
      window.removeEventListener("blur", handlePageState);
    };
  }, [containerRef, eventPrefix, geometryEventType, threshold, thresholdKey]);

  return elementRef;
}
